<template>
  <b-modal
    id="franchise-manual-cycle-modal"
    v-model="isVisible"
    centered
    size="lg"
    :hide-header-close="isBlocking"
    :no-close-on-backdrop="isBlocking"
    :no-close-on-esc="isBlocking"
    @hide="onHide"
  >
    <template #modal-title>
      <span class="font-weight-bold">{{ title }}</span>
    </template>

    <p>{{ body }}</p>

    <b-alert v-if="isNearDeadline" variant="warning" show class="mb-1">
      <div class="alert-body">
        <feather-icon icon="AlertTriangleIcon" class="mr-50" />
        <span class="font-weight-bold">Prazo próximo do vencimento:</span>
        {{ deadlineLabel }}. Após o prazo seu acesso ao Oráculo fica restrito.
      </div>
    </b-alert>

    <!-- Agrupado por envio: o franqueado pode ter mais de um ciclo em aberto
         e todos os manuais pendentes precisam ficar visíveis. -->
    <div v-for="group in groups" :key="group.id" class="mb-1">
      <small v-if="groups.length > 1" class="text-muted d-block mb-25">
        Envio de {{ group.sent_at | dateTime }}
        <span v-if="group.deadline_label"> — {{ group.deadline_label }}</span>
      </small>
      <ul class="list-unstyled mb-0">
        <li v-for="item in group.items" :key="`${group.id}-${item.manual_id}`" class="mb-50">
          <feather-icon icon="FileTextIcon" class="mr-50" />
          <a :href="item.file_url" target="_blank" rel="noopener">
            {{ item.name }} — versão {{ item.version }}
          </a>
          <b-badge v-if="item.is_mandatory" variant="light-danger" class="ml-50">Obrigatório</b-badge>
        </li>
      </ul>
    </div>

    <template v-if="step === 'reading'">
      <hr />
      <p class="font-weight-bold mb-50">Documentos abrangidos por esta confirmação:</p>
      <ul class="mb-1">
        <li v-for="item in items" :key="`accept-${item.manual_id}-${item.cycle_id}`">
          {{ item.name }} — versão {{ item.version }}
        </li>
      </ul>
      <b-form-checkbox v-model="readingAcknowledged">
        Declaro que li e estou ciente do conteúdo dos Manuais do Franqueado disponibilizados pela Futuro Corp.
      </b-form-checkbox>
    </template>

    <template #modal-footer>
      <!-- Manual complementar: fechar já registra a visualização, sem checkbox. -->
      <b-button
        v-if="step === 'optional'"
        variant="primary"
        :disabled="saving"
        @click="isVisible = false"
      >
        Entendi
      </b-button>
      <template v-else>
        <b-button v-if="!isBlocking" variant="outline-secondary" @click="isVisible = false">
          Continuar para o Oráculo
        </b-button>
        <b-button
          v-if="step === 'receipt'"
          variant="primary"
          :disabled="saving"
          @click="onConfirmReceipt"
        >
          {{ saving ? "Confirmando..." : "Confirmo que recebi e tive acesso aos manuais" }}
        </b-button>
        <b-button
          v-else
          variant="primary"
          :disabled="saving || !readingAcknowledged"
          @click="onConfirmReading"
        >
          {{ saving ? "Confirmando..." : "Confirmar leitura" }}
        </b-button>
      </template>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BButton, BFormCheckbox, BBadge, BAlert } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import * as types from '../store/types'

const BLOCKING_STATUSES = ['blocked', 'expired']
const PENDING_STATUSES = ['awaiting_receipt', 'pending_reading', 'near_deadline', 'expired', 'blocked']

export default {
  name: 'FranchiseManualCycleModal',
  components: { BModal, BButton, BFormCheckbox, BBadge, BAlert },
  data() {
    return {
      isVisible: false,
      saving: false,
      readingAcknowledged: false,
      acknowledging: false,
    }
  },
  setup() {
    return { toast: useToast() }
  },
  computed: {
    ...mapGetters({
      mandatoryCycles: types.MY_FRANCHISE_MANUAL_CYCLE,
      optionalCycles: types.MY_OPTIONAL_FRANCHISE_MANUAL_CYCLE,
    }),
    // Ciclos que de fato exigem ação. Enviados com falha ficam de fora: o
    // franqueado não recebeu o e-mail e não há o que cobrar dele.
    pendingMandatory() {
      return (this.mandatoryCycles || []).filter((cycle) => PENDING_STATUSES.includes(cycle.status))
    },
    pendingOptional() {
      return (this.optionalCycles || []).filter(
        (cycle) => cycle.sent_status === 'sent' && !cycle.reading_confirmed_at
      )
    },
    // O obrigatório tem precedência: o complementar só aparece quando não há
    // nada obrigatório pendente, para não empilhar dois modais.
    groups() {
      return this.pendingMandatory.length ? this.pendingMandatory : this.pendingOptional
    },
    isMandatoryStep() {
      return this.pendingMandatory.length > 0
    },
    items() {
      return this.groups.flatMap((cycle) => (cycle.items || []).map(
        (item) => ({ ...item, cycle_id: cycle.id })
      ))
    },
    step() {
      if (!this.groups.length) return null
      if (!this.isMandatoryStep) return 'optional'
      // Só passa para a leitura quando todos os ciclos abertos tiveram o
      // recebimento confirmado — o backend exige isso de todos.
      return this.groups.every((cycle) => cycle.receipt_confirmed_at) ? 'reading' : 'receipt'
    },
    // Qualquer ciclo aberto vencido trava o acesso, não só o mais recente.
    isBlocking() {
      return this.isMandatoryStep
        && this.pendingMandatory.some((cycle) => BLOCKING_STATUSES.includes(cycle.status))
    },
    isNearDeadline() {
      return this.isMandatoryStep
        && this.pendingMandatory.some((cycle) => cycle.status === 'near_deadline')
    },
    // O prazo mais apertado entre os ciclos abertos é o que vale para o aviso.
    mostUrgentCycle() {
      return this.pendingMandatory
        .filter((cycle) => cycle.days_remaining !== null && cycle.days_remaining !== undefined)
        .sort((a, b) => a.days_remaining - b.days_remaining)[0]
    },
    deadlineLabel() {
      return this.mostUrgentCycle?.deadline_label || null
    },
    shouldShow() {
      return this.groups.length > 0
    },
    title() {
      if (this.step === 'optional') {
        return 'Novo manual complementar disponível'
      }
      if (this.isBlocking) {
        return 'Acesso restrito — Manuais do Franqueado'
      }
      if (this.step === 'receipt') {
        return 'Manuais do Franqueado disponíveis'
      }
      return 'Confirme a leitura dos Manuais do Franqueado'
    },
    body() {
      if (this.step === 'optional') {
        return 'Estes manuais são complementares: a leitura é recomendada, mas não exige confirmação '
          + 'nem tem prazo. Ao fechar esta janela o aviso não será exibido novamente.'
      }
      if (this.isBlocking) {
        return 'Seu prazo para confirmação de leitura dos Manuais do Franqueado foi encerrado. '
          + 'Para continuar utilizando o Oráculo, conclua a leitura e confirme abaixo.'
      }
      if (this.step === 'receipt') {
        return 'Confirme que recebeu e teve acesso aos manuais abaixo, disponibilizados pela Futuro Corp.'
      }
      return this.deadlineLabel
        ? `${this.deadlineLabel} para concluir e confirmar a leitura obrigatória.`
        : 'Revise os manuais abaixo e confirme a leitura obrigatória.'
    },
  },
  watch: {
    shouldShow: {
      immediate: true,
      handler(val) {
        this.isVisible = val
      },
    },
  },
  created() {
    this.fetchMyCycle().catch(() => {})
  },
  methods: {
    ...mapActions({
      fetchMyCycle: types.GET_MY_FRANCHISE_MANUAL_CYCLE,
      confirmReceipt: types.CONFIRM_FRANCHISE_MANUAL_RECEIPT,
      confirmReading: types.CONFIRM_FRANCHISE_MANUAL_READING,
      acknowledgeOptional: types.ACKNOWLEDGE_OPTIONAL_FRANCHISE_MANUALS,
    }),
    // Fechar o modal do complementar é o próprio registro de leitura.
    onHide() {
      if (this.step !== 'optional' || this.acknowledging) return
      this.acknowledging = true
      this.acknowledgeOptional()
        .catch(() => {})
        .finally(() => {
          this.acknowledging = false
        })
    },
    onConfirmReceipt() {
      this.saving = true
      this.confirmReceipt()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao confirmar o recebimento. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
    onConfirmReading() {
      this.saving = true
      this.confirmReading()
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso',
              text: 'Leitura dos Manuais do Franqueado confirmada!',
              icon: 'CoffeeIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao confirmar a leitura. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>
